import React, { FC, useState } from 'react';
import { Alert, Box, Card, Divider, Flex, Heading, Text } from '@amzn/storm-ui';
import styled from 'styled-components';
import useIntl from '@i18n/useIntl';
import {
  COMBINE_BILLING_DETAILS_HEADING_KEY,
  COMBINE_BILLING_DETAILS_DESCRIPTION_KEY,
  COMBINE_BILLING_DETAILS_ALERT_KEY,
  COMBINE_BILLING_TAB_SA_KEY,
  COMBINE_BILLING_TAB_DSP_KEY,
  DSP_BILLING_FALLBACK,
} from '../constants';
import BillingSummaryTab from './BillingSummaryTab';
import type { BillingTabData } from '../types';

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #d9dee4;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  color: ${({ $active }) => ($active ? '#161D26' : '#5C687A')};
  border-bottom: ${({ $active }) =>
    $active ? '2px solid #161D26' : '2px solid transparent'};
  &:hover {
    color: #161d26;
  }
`;

interface BillingDetailsCardProps {
  sponsoredAdsBilling: BillingTabData | null;
  amazonDspBilling: BillingTabData | null;
}

type BillingTab = 'SA' | 'DSP';

const BillingDetailsCard: FC<BillingDetailsCardProps> = ({
  sponsoredAdsBilling,
  amazonDspBilling,
}) => {
  const { intl } = useIntl();
  const [activeTab, setActiveTab] = useState<BillingTab>(
    sponsoredAdsBilling ? 'SA' : 'DSP',
  );
  const [alertDismissed, setAlertDismissed] = useState(false);

  // DSP accounts in the combine flow are always invoiced to the Manager
  // Account, so the DSP tab always renders: when the backend returns no DSP
  // billing data (for example, the caller lacks MA access) the tab falls back
  // to showing only the invoicing type, with no address or payment rows.
  const dspBilling = amazonDspBilling ?? DSP_BILLING_FALLBACK;

  const activeData = activeTab === 'SA' ? sponsoredAdsBilling : dspBilling;

  return (
    <Card padding="none">
      <Box
        paddingInlineStart="medium"
        paddingInlineEnd="medium"
        paddingBlockStart="medium"
        paddingBlockEnd="small"
      >
        <Heading renderAs="h4" typeface="display">
          {intl.getMessage(COMBINE_BILLING_DETAILS_HEADING_KEY)}
        </Heading>
        <Text color="secondary">
          {intl.getMessage(COMBINE_BILLING_DETAILS_DESCRIPTION_KEY)}
        </Text>
      </Box>
      <Divider marginBlockStart="none" marginBlockEnd="none" />
      <Box
        paddingInlineStart="large"
        paddingInlineEnd="large"
        paddingBlockStart="large"
        paddingBlockEnd="large"
      >
        <Flex direction="column" rowGap="base" alignItems="stretch">
          {!alertDismissed && (
            <Alert
              variant="info"
              withCloseButton
              onClose={() => setAlertDismissed(true)}
            >
              {intl.getMessage(COMBINE_BILLING_DETAILS_ALERT_KEY)}
            </Alert>
          )}

          <div>
            <TabContainer role="tablist">
              {sponsoredAdsBilling && (
                <TabButton
                  $active={activeTab === 'SA'}
                  onClick={() => setActiveTab('SA')}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'SA'}
                  id="tab-sa"
                  aria-controls="tabpanel-sa"
                >
                  {intl.getMessage(COMBINE_BILLING_TAB_SA_KEY)}
                </TabButton>
              )}
              <TabButton
                $active={activeTab === 'DSP'}
                onClick={() => setActiveTab('DSP')}
                type="button"
                role="tab"
                aria-selected={activeTab === 'DSP'}
                id="tab-dsp"
                aria-controls="tabpanel-dsp"
              >
                {intl.getMessage(COMBINE_BILLING_TAB_DSP_KEY)}
              </TabButton>
            </TabContainer>

            {activeData && (
              <div
                role="tabpanel"
                id={`tabpanel-${activeTab.toLowerCase()}`}
                aria-labelledby={`tab-${activeTab.toLowerCase()}`}
              >
                <BillingSummaryTab
                  data={activeData}
                  tabId={`billing-summary-${activeTab.toLowerCase()}`}
                />
              </div>
            )}
          </div>
        </Flex>
      </Box>
    </Card>
  );
};

export default BillingDetailsCard;
